import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function ProfilePage() {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    api.get('/users/me/profile').then(r => {
      setProfile(r.data.user);
      setForm({ firstName: r.data.user.first_name, lastName: r.data.user.last_name, phone: r.data.user.phone || '', licenseNumber: r.data.user.license_number || '' });
    }).finally(() => setLoading(false));
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true); setMessage(''); setError('');
    try {
      await api.put('/users/me/profile', form);
      setMessage('Profile updated successfully');
    } catch { setError('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) { setPwError('Passwords do not match'); return; }
    if (pwForm.newPassword.length < 8) { setPwError('Password must be at least 8 characters'); return; }
    setPwSaving(true); setPwError('');
    try {
      await api.put('/users/me/password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
      setMessage('Password updated');
    } catch (err) { setPwError(err.response?.data?.error || 'Failed'); }
    finally { setPwSaving(false); }
  };

  if (loading) return <div className="loading-wrapper"><div className="spinner" /></div>;

  return (
    <div className="page-wrapper">
      <div className="container" style={{ maxWidth: 720 }}>
        <h1 className="page-title" style={{ marginBottom: 8 }}>My Profile</h1>
        <p className="page-subtitle" style={{ marginBottom: 32 }}>{profile?.email}</p>

        {message && <div className="alert alert-success" style={{ marginBottom: 24 }}>{message}</div>}

        {/* Profile form */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Personal Information</h2>
          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          <form onSubmit={saveProfile} style={styles.form}>
            <div style={styles.row}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">First Name</label>
                <input className="input" value={form.firstName || ''} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Last Name</label>
                <input className="input" value={form.lastName || ''} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="input" value={profile?.email || ''} disabled style={{ background: 'var(--slate-50)', color: 'var(--slate-400)' }} />
            </div>
            <div style={styles.row}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Phone</label>
                <input className="input" value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(480) 555-0100" />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">ADRE License #</label>
                <input className="input" value={form.licenseNumber || ''} onChange={e => setForm(f => ({ ...f, licenseNumber: e.target.value }))} placeholder="SA12345678" />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </form>
        </div>

        {/* Password form */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Change Password</h2>
          {pwError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{pwError}</div>}
          <form onSubmit={savePassword} style={styles.form}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input className="input" type="password" value={pwForm.currentPassword} onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} required />
            </div>
            <div style={styles.row}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">New Password</label>
                <input className="input" type="password" placeholder="Min. 8 characters" value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} required />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Confirm New Password</label>
                <input className="input" type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} required />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={pwSaving}>{pwSaving ? 'Updating...' : 'Update Password'}</button>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  section: { background: 'white', border: '1px solid var(--color-border)', borderRadius: 16, padding: '28px 32px', marginBottom: 24 },
  sectionTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'var(--slate-900)' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  row: { display: 'flex', gap: 16 },
};
